var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { mockData as calendarEvents } from '@web/lib/demo/data/calendarEvents';
import { mockData as chatEvents } from '@web/lib/demo/data/chatEvents';
import { mockData as meetingEvents } from '@web/lib/demo/data/meetingEvents';
import { filterData, getOrgUnits, getUserOrgUnits, getUsers } from '@web/lib/demo/utils';
import { AnalyticsEntityGroupByEnum, ApplicationGoogleEnum } from '@workalizer/shared/enums';
import { averageTimeSpentByActivityTypePeriods, processTimeSpentOnWorkActivities, sumTimeSpentByActivityTypePeriodTotal } from '@workalizer/shared/services/processTimeSpentOnWorkActivities';
import { getPreviousPeriod } from '@workalizer/shared/utils';
import { addDays, differenceInDays } from 'date-fns';
var CHAT_DURATION_SECONDS = 3 * 60;
var DOCUMENT_DURATION_SECONDS = 5 * 60;
var demoEvents = __spreadArray(__spreadArray(__spreadArray([], chatEvents, true), meetingEvents, true), calendarEvents, true);
var ACTIVITY_TYPE_DEMO_MINUTES = (_a = {},
    _a[ApplicationGoogleEnum.gmail] = { minutes: 32 },
    _a[ApplicationGoogleEnum.chat] = { minutes: 24 },
    _a[ApplicationGoogleEnum.drive] = { minutes: 42 },
    _a[ApplicationGoogleEnum.meet] = { minutes: 68, deduped: true },
    _a[ApplicationGoogleEnum.calendar] = { minutes: 36, deduped: true },
    _a[ApplicationGoogleEnum.gemini] = { minutes: 18 },
    _a);
var seedActivityTypeDemoRows = function (userIds, dateFrom, dateTo, periodScale) {
    if (periodScale === void 0) { periodScale = 1; }
    var start = new Date(dateFrom);
    var end = new Date(dateTo);
    var dayCount = Math.max(differenceInDays(end, start), 0);
    var rows = [];
    var _loop_1 = function (userId) {
        var userFactor = 0.75 + (userId % 5) * 0.22;
        var _loop_2 = function (dayOffset) {
            var date = addDays(start, dayOffset);
            var dayFactor = 0.82 + (dayOffset % 7) * 0.1;
            var scale = periodScale * userFactor * dayFactor;
            Object.entries(ACTIVITY_TYPE_DEMO_MINUTES).forEach(function (_a, typeIndex) {
                var type = _a[0], config = _a[1];
                var durationSeconds = Math.round(config.minutes * 60 * scale);
                if (durationSeconds <= 0) {
                    return;
                }
                rows.push(__assign({ userId: userId, date: date, durationSeconds: durationSeconds, type: type }, (config.deduped
                    ? {
                        dedupedDurationSeconds: durationSeconds,
                        dedupeKey: userId * 10000 + dayOffset * 100 + typeIndex
                    }
                    : { dedupeKey: null })));
            });
        };
        for (var dayOffset = 0; dayOffset <= dayCount; dayOffset++) {
            _loop_2(dayOffset);
        }
    };
    for (var _i = 0, userIds_1 = userIds; _i < userIds_1.length; _i++) {
        var userId = userIds_1[_i];
        _loop_1(userId);
    }
    return rows;
};
var buildRowsForPeriod = function (userIds, fallbackUserId, dateFrom, dateTo, periodScale) {
    var _a, _b, _c;
    if (periodScale === void 0) { periodScale = 1; }
    var data = filterData(demoEvents, [
        {
            key: 'date',
            value: dateFrom,
            operator: 'gt'
        },
        {
            key: 'date',
            value: dateTo,
            operator: 'lt'
        }
    ]).filter(function (event) { return userIds.has(event.userId); });
    var rows = [];
    for (var _i = 0, data_1 = data; _i < data_1.length; _i++) {
        var event = data_1[_i];
        if ('meetingId' in event &&
            event.meetingId &&
            'action' in event &&
            event.action === 'MEET_CALL_ENDED') {
            var meetingDuration = 'meeting' in event && ((_a = event.meeting) === null || _a === void 0 ? void 0 : _a.durationSeconds) != null
                ? event.meeting.durationSeconds
                : ((_b = event.durationSeconds) !== null && _b !== void 0 ? _b : 1800);
            rows.push({
                userId: event.userId,
                date: new Date(event.date),
                durationSeconds: (_c = event.durationSeconds) !== null && _c !== void 0 ? _c : meetingDuration,
                dedupedDurationSeconds: meetingDuration,
                type: ApplicationGoogleEnum.meet,
                dedupeKey: event.meetingId
            });
            continue;
        }
        if ('meetingId' in event && event.meetingId) {
            continue;
        }
        if ('calendarId' in event && event.calendarId) {
            var calendarDuration = 3600;
            rows.push({
                userId: event.userId,
                date: new Date(event.date),
                durationSeconds: calendarDuration,
                dedupedDurationSeconds: calendarDuration,
                type: ApplicationGoogleEnum.calendar,
                dedupeKey: event.calendarId
            });
            continue;
        }
        rows.push({
            userId: event.userId,
            date: new Date(event.date),
            durationSeconds: CHAT_DURATION_SECONDS,
            type: ApplicationGoogleEnum.chat,
            dedupeKey: null
        });
    }
    if (!rows.length) {
        rows.push({
            userId: fallbackUserId,
            date: new Date(dateFrom),
            durationSeconds: DOCUMENT_DURATION_SECONDS,
            type: ApplicationGoogleEnum.drive,
            dedupeKey: null
        });
    }
    rows.push.apply(rows, seedActivityTypeDemoRows(Array.from(userIds), dateFrom, dateTo, periodScale));
    return rows;
};
export var timeSpentOnWorkActivities = function (input) {
    var _a, _b, _c, _d;
    var users = getUsers({
        orgUnitId: input.orgUnitId,
        userId: input.userId
    });
    var orgUnits = input.orgUnitId || input.userId
        ? []
        : getOrgUnits({
            orgUnitId: input.orgUnitId
        });
    var userOrgUnits = getUserOrgUnits();
    var userIds = new Set(users.map(function (user) { return user.id; }));
    var fallbackUserId = (_b = (_a = users[0]) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : 1;
    var entityGroupBy = input.orgUnitId || input.userId
        ? AnalyticsEntityGroupByEnum.user
        : AnalyticsEntityGroupByEnum.orgUnit;
    var processPeriod = function (dateFrom, dateTo, periodUsers, periodUserIds, periodScale) {
        if (periodUsers === void 0) { periodUsers = users; }
        if (periodUserIds === void 0) { periodUserIds = userIds; }
        if (periodScale === void 0) { periodScale = 1; }
        return processTimeSpentOnWorkActivities(buildRowsForPeriod(periodUserIds, fallbackUserId, dateFrom, dateTo, periodScale), new Date(dateFrom), new Date(dateTo), input.groupBy, entityGroupBy, orgUnits, periodUsers, userOrgUnits);
    };
    var _e = getPreviousPeriod(input.dateFrom, input.dateTo), prevPeriodDateFrom = _e.prevPeriodDateFrom, prevPeriodDateTo = _e.prevPeriodDateTo;
    var current = processPeriod(input.dateFrom, input.dateTo, users, userIds, 1.85);
    var previous = processPeriod(prevPeriodDateFrom, prevPeriodDateTo, users, userIds, 0.38);
    if (!input.userId) {
        return { current: current, previous: previous };
    }
    var teamUsers = getUsers({ orgUnitId: input.orgUnitId }).filter(function (user) { return user.id !== input.userId; });
    var teamUserIds = new Set(teamUsers.map(function (user) { return user.id; }));
    var teamCurrent = processTimeSpentOnWorkActivities(buildRowsForPeriod(teamUserIds, (_d = (_c = teamUsers[0]) === null || _c === void 0 ? void 0 : _c.id) !== null && _d !== void 0 ? _d : fallbackUserId, input.dateFrom, input.dateTo, 1.45), new Date(input.dateFrom), new Date(input.dateTo), input.groupBy, AnalyticsEntityGroupByEnum.user, [], teamUsers, userOrgUnits);
    var teamSize = Math.max(teamUsers.length, 1);
    var teamAverage = current.byType.map(function (period) {
        var teamPeriod = teamCurrent.byType.find(function (item) { return item.name === period.name; });
        var total = teamPeriod
            ? sumTimeSpentByActivityTypePeriodTotal(teamPeriod)
            : 0;
        return {
            date: period.name,
            duration: Math.round(total / teamSize)
        };
    });
    return {
        current: current,
        previous: previous,
        teamAverage: teamAverage,
        teamAverageByType: averageTimeSpentByActivityTypePeriods(teamCurrent.byType, teamSize)
    };
};
